import React from 'react'
import { useTranslation } from 'react-i18next'
import { MDXRenderer } from 'gatsby-plugin-mdx'

import { Header } from '../common/header'
import { Link } from '../common/link'
import { helpCenterPath, useHelpCenter } from './use-help-center'
import './index.sass'

const ARTICLES_PREVIEW_LIMIT = 3

const Hero = ({ country, title, subtitle, breadcrumbs = [] }) => (
  <div className="help-center__hero">
    <Header
      withWhiteLogo
      linkColor="#FFFFFF"
      buttonColor="#FFFFFF"
      activeLinkColor="#FFFFFF"
      country={country} />
    <div className="help-center__hero__inner">
      {breadcrumbs.length > 0 && (
        <nav className="help-center__breadcrumbs">
          {breadcrumbs.map(breadcrumb => (
            <React.Fragment key={breadcrumb.to}>
              <Link to={breadcrumb.to} className="help-center__breadcrumbs__link">{breadcrumb.title}</Link>
              <span className="help-center__breadcrumbs__separator">›</span>
            </React.Fragment>
          ))}
        </nav>
      )}
      <h1 className="help-center__hero__title">{title}</h1>
      {subtitle && <span className="help-center__hero__subtitle">{subtitle}</span>}
    </div>
  </div>
)

const FolderCard = ({ country, category, folder }) => {
  const { t } = useTranslation()
  const folderPath = helpCenterPath(category.slug, folder.slug)

  return (
    <div className="help-center__folder-card">
      <Link to={folderPath} className="help-center__folder-card__title">
        {folder.title} ({folder.articles.length})
      </Link>
      <ul className="help-center__article-list">
        {folder.articles.slice(0, ARTICLES_PREVIEW_LIMIT).map(article => (
          <li key={article.article_slug}>
            <Link
              to={helpCenterPath(category.slug, folder.slug, article.article_slug)}
              className="help-center__article-link">
              {article.title}
            </Link>
          </li>
        ))}
      </ul>
      {folder.articles.length > ARTICLES_PREVIEW_LIMIT && (
        <Link to={folderPath} className="help-center__view-all">
          {t(`${country}.help_center.view_all`, { count: folder.articles.length })}
        </Link>
      )}
    </div>
  )
}

const FolderGrid = ({ country, category }) => (
  <div className="help-center__folders">
    {category.folders.map(folder => (
      <FolderCard key={folder.slug} country={country} category={category} folder={folder} />
    ))}
  </div>
)

export const HelpCenterIndex = ({ country }) => {
  const { t } = useTranslation()
  const categories = useHelpCenter(country)

  return (
    <div className="help-center">
      <Hero
        country={country}
        title={t(`${country}.help_center.title`)}
        subtitle={t(`${country}.help.secondary_title`)} />
      <div className="help-center__content">
        {categories.map(category => (
          <section key={category.slug} className="help-center__category">
            <h2 className="help-center__category__title">
              <Link to={helpCenterPath(category.slug)}>{category.title}</Link>
            </h2>
            <FolderGrid country={country} category={category} />
          </section>
        ))}
      </div>
    </div>
  )
}

export const HelpCenterCategory = ({ country, categorySlug }) => {
  const { t } = useTranslation()
  const category = useHelpCenter(country).find(item => item.slug === categorySlug)

  return (
    <div className="help-center">
      <Hero
        country={country}
        title={category.title}
        breadcrumbs={[{ title: t(`${country}.help_center.title`), to: helpCenterPath() }]} />
      <div className="help-center__content">
        <FolderGrid country={country} category={category} />
      </div>
    </div>
  )
}

export const HelpCenterFolder = ({ country, categorySlug, folderSlug }) => {
  const { t } = useTranslation()
  const category = useHelpCenter(country).find(item => item.slug === categorySlug)
  const folder = category.folders.find(item => item.slug === folderSlug)

  return (
    <div className="help-center">
      <Hero
        country={country}
        title={folder.title}
        breadcrumbs={[
          { title: t(`${country}.help_center.title`), to: helpCenterPath() },
          { title: category.title, to: helpCenterPath(category.slug) },
        ]} />
      <div className="help-center__content">
        <ul className="help-center__article-list help-center__article-list--full">
          {folder.articles.map(article => (
            <li key={article.article_slug}>
              <Link
                to={helpCenterPath(category.slug, folder.slug, article.article_slug)}
                className="help-center__article-link">
                {article.title}
              </Link>
              <span className="help-center__article-date">
                {t(`${country}.help_center.modified_on`, { date: article.last_updated_at })}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}

export const HelpCenterArticle = ({ country, categorySlug, folderSlug, articleSlug, body }) => {
  const { t } = useTranslation()
  const category = useHelpCenter(country).find(item => item.slug === categorySlug)
  const folder = category.folders.find(item => item.slug === folderSlug)
  const article = folder.articles.find(item => item.article_slug === articleSlug)
  const otherArticles = folder.articles.filter(item => item.article_slug !== articleSlug)

  return (
    <div className="help-center">
      <Hero
        country={country}
        title={article.title}
        subtitle={t(`${country}.help_center.modified_on`, { date: article.last_updated_at })}
        breadcrumbs={[
          { title: t(`${country}.help_center.title`), to: helpCenterPath() },
          { title: category.title, to: helpCenterPath(category.slug) },
          { title: folder.title, to: helpCenterPath(category.slug, folder.slug) },
        ]} />
      <div className="help-center__content help-center__article-layout">
        <article className="help-center__article-body">
          <MDXRenderer>{body}</MDXRenderer>
        </article>
        {otherArticles.length > 0 && (
          <aside className="help-center__related">
            <h3 className="help-center__related__title">{t(`${country}.help_center.other_articles`)}</h3>
            <ul className="help-center__article-list">
              {otherArticles.map(item => (
                <li key={item.article_slug}>
                  <Link
                    to={helpCenterPath(category.slug, folder.slug, item.article_slug)}
                    className="help-center__article-link">
                    {item.title}
                  </Link>
                </li>
              ))}
            </ul>
          </aside>
        )}
      </div>
    </div>
  )
}
